import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Eye, Camera, Video, Snowflake, RotateCcw, Zap, ZapOff,
  PhoneOff, ShieldCheck, AlertCircle, Loader2,
} from 'lucide-react';
import AnnotationOverlay from '../components/AnnotationOverlay';
import { api } from '../lib/api';
import { Signaling } from '../lib/signaling';
import {
  createPeer, buildIceServers, getCameraStream,
  replaceVideoTrack, setTorch, captureHiResStill,
} from '../lib/webrtc';

// States: idle → permissions → joining → live → frozen → ended/error
export default function Customer({ sessionId, token }) {
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [techConnected, setTechConnected] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [annotationStrokes, setAnnotationStrokes] = useState([]);
  const [baseW, setBaseW] = useState(0);
  const [baseH, setBaseH] = useState(0);
  const [facing, setFacing] = useState('environment');
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);

  const videoRef = useRef(null);
  const frozenRef = useRef(null);
  const streamRef = useRef(null);
  const pcRef = useRef(null);
  const sigRef = useRef(null);
  const ctrlRef = useRef(null);
  const facingRef = useRef(facing);
  facingRef.current = facing;

  // Fetch session metadata up-front so we can show technician name
  useEffect(() => {
    api.getSessionPublic(sessionId, token)
      .then(setMeta)
      .catch((e) => setError(e.message));
  }, [sessionId, token]);

  function cleanup() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    if (sigRef.current) { sigRef.current.close(); sigRef.current = null; }
  }

  useEffect(() => () => cleanup(), []);

  const sendCtrl = useCallback((type, payload = {}) => {
    const ch = ctrlRef.current;
    if (!ch || ch.readyState !== 'open') return;
    try { ch.send(JSON.stringify({ type, ...payload })); } catch {}
  }, []);

  // Default starting bitrate cap (kbps). Tech can override at any time.
  const DEFAULT_MAX_BITRATE_KBPS = 3000;

  // Apply (or update) the video sender's quality settings: prefer resolution
  // over framerate when bandwidth is constrained, and apply a max bitrate.
  // kbps === 0 / null means "uncap".
  const applySenderQuality = useCallback(async (kbps) => {
    const pc = pcRef.current;
    if (!pc) return;
    const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'video');
    if (!sender) return;
    try {
      const params = sender.getParameters();
      params.degradationPreference = 'maintain-resolution';
      if (!params.encodings || !params.encodings.length) {
        params.encodings = [{}];
      }
      const bps = kbps ? kbps * 1000 : null;
      for (const enc of params.encodings) {
        if (bps) enc.maxBitrate = bps;
        else delete enc.maxBitrate;
      }
      await sender.setParameters(params);
    } catch (e) {
      console.warn('setParameters failed', e);
    }
  }, []);

  // Read the post-rotation frame size from the local video element and push
  // it to the technician. videoWidth/videoHeight reflect what the user
  // actually sees on screen (and what the tech receives) — track.getSettings()
  // returns the unrotated camera sensor dimensions, which often disagrees on
  // phones held in portrait, throwing annotation coordinates off.
  const pushFrameInfo = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const w = v.videoWidth;
    const h = v.videoHeight;
    if (!w || !h) return;
    setBaseW(w);
    setBaseH(h);
    sendCtrl('frame-info', { width: w, height: h });
  }, [sendCtrl]);

  // Re-push frame info whenever the video element actually has dimensions
  // (loadedmetadata) and whenever they change (resize on rotation/track swap).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const handler = () => pushFrameInfo();
    v.addEventListener('loadedmetadata', handler);
    v.addEventListener('resize', handler);
    return () => {
      v.removeEventListener('loadedmetadata', handler);
      v.removeEventListener('resize', handler);
    };
  }, [pushFrameInfo, phase]);

  async function startSession() {
    setError(null);
    setPhase('permissions');
    let stream;
    try {
      stream = await getCameraStream({ facingMode: facingRef.current });
      streamRef.current = stream;
    } catch (e) {
      setError(e.message || 'Camera/microphone permission was denied. Please grant access and try again.');
      setPhase('idle');
      return;
    }

    const videoTrack = stream.getVideoTracks()[0];
    setTorchSupported(!!(videoTrack?.getCapabilities && videoTrack.getCapabilities().torch));

    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true; // local preview muted to avoid echo
    }

    setPhase('joining');

    const sig = new Signaling(sessionId, 'customer', token);
    sigRef.current = sig;

    // Build (or rebuild) the PeerConnection. Done lazily on peer-joined and
    // on incoming offer rather than eagerly at startSession, so a customer
    // who taps the link before the tech has opened the viewer doesn't end
    // up with a stale PC by the time the tech finally arrives (e.g. after
    // the phone backgrounded the page or the prior PC went into 'failed').
    const buildPc = () => {
      if (pcRef.current) {
        try { pcRef.current.close(); } catch {}
      }
      const pc = createPeer({
        iceServers: buildIceServers(),
        onIce: (cand) => sig.send('ice', { candidate: cand }),
        onState: (state) => {
          if (state === 'connected' || state === 'completed') setPhase('live');
          if (state === 'disconnected') {/* transient */}
          if (state === 'failed') setError('Connection lost. Please refresh to reconnect.');
          if (state === 'closed') setPhase('ended');
        },
        onChannel: (ch) => {
          ctrlRef.current = ch;
          ch.onopen = () => pushFrameInfo();
          ch.onmessage = (e) => handleCtrl(e.data);
        },
      });
      pcRef.current = pc;
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => pc.addTrack(t, s));
      // Apply default quality settings now so the encoder starts in
      // "maintain-resolution" mode rather than the browser default
      // ("balanced", which drops resolution to keep framerate). The tech
      // will send their own cap shortly via the data channel.
      applySenderQuality(DEFAULT_MAX_BITRATE_KBPS);
      return pc;
    };

    sig.on('peer-joined', () => {
      setTechConnected(true);
      buildPc();
    });
    sig.on('peer-left', () => {
      setTechConnected(false);
      setPhase('joining');
      if (pcRef.current) {
        try { pcRef.current.close(); } catch {}
        pcRef.current = null;
      }
    });
    sig.on('offer', async (msg) => {
      const pc = pcRef.current || buildPc();
      try {
        await pc.setRemoteDescription({ type: 'offer', sdp: msg.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sig.send('answer', { sdp: answer.sdp });
      } catch (e) {
        console.warn('answer flow failed', e);
        setError('Could not set up the call. Please refresh to retry.');
      }
    });
    sig.on('ice', async (msg) => {
      if (!msg.candidate) return;
      const pc = pcRef.current;
      if (!pc) return;
      try { await pc.addIceCandidate(msg.candidate); } catch {}
    });
    sig.on('error', () => setError('Connection trouble — retrying…'));
    sig.connect();
  }

  async function handleCtrl(data) {
    let msg; try { msg = JSON.parse(data); } catch { return; }
    if (msg.type === 'freeze') {
      const v = videoRef.current;
      const cv = frozenRef.current;
      if (v && cv) {
        cv.width = v.videoWidth;
        cv.height = v.videoHeight;
        cv.getContext('2d').drawImage(v, 0, 0);
        setFrozen(true);
      }
    } else if (msg.type === 'unfreeze') {
      setFrozen(false);
      setAnnotationStrokes([]); // clear residual annotations on resume
    } else if (msg.type === 'annotation') {
      setAnnotationStrokes(msg.strokes || []);
    } else if (msg.type === 'max-bitrate') {
      // Tech is asking us to cap the upload bitrate. kbps === 0 means uncap.
      await applySenderQuality(msg.kbps || 0);
    } else if (msg.type === 'capture-request') {
      // Take a high-res still and send back to technician
      const track = streamRef.current?.getVideoTracks?.()[0];
      try {
        const blob = await captureHiResStill(track);
        if (blob) {
          await api.uploadCapture(sessionId, token, blob, 'capture-hires', `customer-${Date.now()}.jpg`);
        }
      } catch {}
    } else if (msg.type === 'end') {
      setPhase('ended');
      cleanup();
    }
  }

  async function flipCamera() {
    const next = facing === 'environment' ? 'user' : 'environment';
    setFacing(next);
    if (!streamRef.current || !pcRef.current) return;
    try {
      const newTrack = await replaceVideoTrack(pcRef.current, streamRef.current, next);
      setTorchSupported(!!(newTrack?.getCapabilities && newTrack.getCapabilities().torch));
      setTorchOn(false);
    } catch (e) {
      setError('Could not switch cameras on this device.');
    }
  }

  async function toggleTorch() {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    const ok = await setTorch(track, !torchOn).catch(() => false);
    if (ok) setTorchOn((v) => !v);
  }

  function endSession() {
    if (!window.confirm('End the support session?')) return;
    sendCtrl('end');
    cleanup();
    setPhase('ended');
  }

  if (phase === 'ended') {
    return <EndedScreen />;
  }

  if (phase === 'idle') {
    return (
      <Permission
        meta={meta}
        error={error}
        onStart={startSession}
        onDismissError={() => setError(null)}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black overflow-hidden flex flex-col">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={`absolute inset-0 w-full h-full object-contain ${frozen ? 'opacity-0' : ''}`}
      />
      <canvas
        ref={frozenRef}
        className={`absolute inset-0 w-full h-full ${frozen ? '' : 'hidden'}`}
        style={{ objectFit: 'contain', width: '100%', height: '100%' }}
      />
      <AnnotationOverlay strokes={annotationStrokes} baseW={baseW} baseH={baseH} />

      {/* Top status bar */}
      <div className="relative z-10 px-4 pt-[calc(env(safe-area-inset-top)+12px)] pb-3 bg-gradient-to-b from-black/70 to-transparent flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#f7953c] rounded-lg flex items-center justify-center">
            <Eye className="w-5 h-5 text-white" />
          </div>
          <div className="text-white font-medium text-sm">Clarity IT Remote Help</div>
        </div>
        <StatusPill phase={phase} techConnected={techConnected} frozen={frozen} />
      </div>

      <div className="flex-1" />

      {/* Bottom action bar */}
      <div className="relative z-10 px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-3 bg-gradient-to-t from-black/80 to-transparent">
        {error && (
          <div className="mx-auto max-w-md mb-3 bg-red-500/20 border border-red-500/40 rounded-lg p-2 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-100">{error}</div>
          </div>
        )}

        <div className="flex items-center justify-center gap-3">
          <RoundBtn onClick={flipCamera} title="Switch camera">
            <RotateCcw className="w-5 h-5" />
          </RoundBtn>
          <RoundBtn
            onClick={toggleTorch}
            disabled={!torchSupported}
            title="Torch"
            active={torchOn}
          >
            {torchOn ? <Zap className="w-5 h-5" /> : <ZapOff className="w-5 h-5" />}
          </RoundBtn>
          <button
            onClick={endSession}
            className="px-5 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full inline-flex items-center gap-2 font-medium shadow-lg"
          >
            <PhoneOff className="w-5 h-5" /> End
          </button>
        </div>
      </div>
    </div>
  );
}

function RoundBtn({ children, onClick, disabled, title, active }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors shadow-md ${
        disabled ? 'bg-slate-800/60 text-slate-600 cursor-not-allowed' :
        active ? 'bg-[#f7953c] text-white' :
        'bg-slate-900/80 text-white hover:bg-slate-800'
      }`}
    >
      {children}
    </button>
  );
}

function StatusPill({ phase, techConnected, frozen }) {
  if (frozen) {
    return (
      <div className="bg-blue-600/90 text-white text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
        <Snowflake className="w-3.5 h-3.5" /> Paused for review
      </div>
    );
  }
  if (phase === 'permissions') {
    return (
      <div className="bg-amber-500/90 text-white text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
        <Camera className="w-3.5 h-3.5" /> Asking for camera…
      </div>
    );
  }
  if (phase === 'joining' || !techConnected) {
    return (
      <div className="bg-amber-500/90 text-white text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Waiting for technician
      </div>
    );
  }
  return (
    <div className="bg-emerald-600/90 text-white text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 bg-white rounded-full recording-dot" /> Live
    </div>
  );
}

function Permission({ meta, error, onStart, onDismissError }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md space-y-5">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#f7953c] rounded-2xl mb-4">
              <Eye className="w-9 h-9 text-white" />
            </div>
            <h1 className="text-2xl font-bold">You're connecting to Clarity IT</h1>
            {meta?.contactName && (
              <p className="text-slate-400 mt-1 text-sm">Hi {meta.contactName} —</p>
            )}
            <p className="text-slate-400 mt-2 text-sm">
              Tap the button below to share your camera with our technician
              {meta?.technicianName ? `, ${meta.technicianName}` : ''}.
            </p>
          </div>

          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-3">
            <Bullet icon={<Camera className="w-4 h-4 text-[#f7953c]" />}>
              Your camera shows live to our technician — they cannot control it.
            </Bullet>
            <Bullet icon={<Video className="w-4 h-4 text-[#f7953c]" />}>
              They can pause the view and circle things to point them out.
            </Bullet>
            <Bullet icon={<ShieldCheck className="w-4 h-4 text-emerald-400" />}>
              The session ends as soon as you tap End — nothing stays running.
            </Bullet>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-300 flex-1">{error}</div>
              <button onClick={onDismissError} className="text-red-300/60 hover:text-red-300 text-sm">
                Dismiss
              </button>
            </div>
          )}

          <button
            onClick={onStart}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#f7953c] hover:bg-[#e88729] text-white font-semibold rounded-xl text-lg transition-colors"
          >
            <Camera className="w-5 h-5" /> Start camera
          </button>

          <div className="text-center text-xs text-slate-500">
            Need help? Call <span className="text-slate-300 font-medium">07 55 000 457</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bullet({ icon, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
    </div>
  );
}

function EndedScreen() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4">
      <div className="text-center max-w-md space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-800 rounded-2xl">
          <PhoneOff className="w-8 h-8 text-slate-300" />
        </div>
        <h1 className="text-2xl font-bold">Session ended</h1>
        <p className="text-slate-400 text-sm">
          Your camera has been turned off. You can close this page.
        </p>
        <p className="text-xs text-slate-600 pt-4">Clarity IT · 07 55 000 457</p>
      </div>
    </div>
  );
}
